/**
 * ExportService
 * Handles data export functionality for datatables
 * Supports CSV and PDF export formats
 */

import { saveAs } from 'file-saver';

export interface ExportColumn {
    name: string;
    label: string;
    selected: boolean;
}

export interface ExportFilter {
    label: string;
    value: string | string[];
}

class ExportService {
    /**
     * Export data to CSV format
     * @param data - Array of data objects to export
     * @param columns - Array of column definitions
     * @param filename - Name of the file to download
     */
    exportToCSV(data: any[], columns: ExportColumn[], filename: string = 'export.csv'): void {
        // Filter to only selected columns
        const selectedColumns = columns.filter(col => col.selected);
        
        if (selectedColumns.length === 0) {
            throw new Error('No columns selected for export');
        }

        // Create CSV header
        const headers = selectedColumns.map(col => this.escapeCSV(col.label));
        const csvContent: string[] = [headers.join(',')];

        // Create CSV rows
        data.forEach(row => {
            const rowData = selectedColumns.map(col => {
                const value = this.getNestedValue(row, col.name);
                return this.escapeCSV(this.formatValue(value));
            });
            csvContent.push(rowData.join(','));
        });

        // Create blob and download
        const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, filename);
    }

    /**
     * Escape special characters for CSV format
     * @param value - Value to escape
     */
    private escapeCSV(value: string): string {
        if (value === null || value === undefined) {
            return '';
        }

        const stringValue = String(value);
        
        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
    }

    /**
     * Format value for export
     * @param value - Value to format
     */
    private formatValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }

        // Handle dates
        if (value instanceof Date) {
            return value.toISOString().split('T')[0];
        }

        // Handle objects (convert to JSON string)
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }

        return String(value);
    }

    /**
     * Get nested object value by string path
     * e.g., "Contact.Company" returns row.Contact.Company
     * @param obj - Object to search
     * @param path - Dot-notation path
     */
    private getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((current, prop) => {
            return current?.[prop];
        }, obj);
    }

    /**
     * Format filters for display in export modal
     * @param filters - Current filter state
     */
    formatFiltersForDisplay(filters: Record<string, any>): ExportFilter[] {
        const displayFilters: ExportFilter[] = [];

        for (const [key, value] of Object.entries(filters)) {
            if (!value || (Array.isArray(value) && value.length === 0)) {
                continue;
            }

            const label = this.formatFilterLabel(key);
            const formattedValue = Array.isArray(value) 
                ? value.map(v => String(v)).join(', ')
                : String(value);

            displayFilters.push({
                label,
                value: formattedValue
            });
        }

        return displayFilters;
    }

    /**
     * Format filter key to human-readable label
     * @param key - Filter key
     */
    private formatFilterLabel(key: string): string {
        // Convert camelCase or snake_case to Title Case
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }
}

const exportService = new ExportService();
export default exportService;
